import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import '@fontsource/aileron/400.css';
import '@fontsource/aileron/600.css';

import './App.css';
import './components/Partners.css';
import './AboutApp.css';
import en from './locales/en.json';
import ar from './locales/ar.json';
import Footer from './components/Footer';
import LandingPage from './pages/LandingPage';
import LogisticsHeader from './logistics/components/LogisticsHeader';
import LogisticsFooter from './logistics/components/LogisticsFooter';
import SiteFavicon from './components/SiteFavicon';

const LogisticsHome = lazy(() => import('./logistics/pages/LogisticsHome'));
const LogisticsServices = lazy(() => import('./logistics/pages/LogisticsServices'));
const LogisticsContact = lazy(() => import('./logistics/pages/LogisticsContact'));
const MarketingApp = lazy(() => import('./marketing/MarketingApp'));
const Projects = lazy(() => import('./pages/Projects'));
const About = lazy(() => import('./pages/About'));
const FreightServices = lazy(() => import('./pages/FreightServices'));
const LogisticsSolutions = lazy(() => import('./pages/LogisticsSolutions'));
const ComprehensiveServices = lazy(() => import('./pages/ComprehensiveServices'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));



const translations = { en, ar };



function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent() {
  const location = useLocation();
  const isMarketingSite = location.pathname.startsWith('/marketing');
  const isStandalonePage = location.pathname === '/' || isMarketingSite;
  const isLogisticsSite = !isStandalonePage;
  const [lang, setLang] = useState('en');
  const [text, setText] = useState(translations[lang]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const savedLang = localStorage.getItem('language') || 'en';
    setLang(savedLang);
  }, []);

  useEffect(() => {
    setText(translations[lang]);
    if (!isMarketingSite) {
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }
  }, [lang, isMarketingSite]);








  const handleLangChange = () => {
    const newLang = lang === 'en' ? 'ar' : 'en';
    setLang(newLang);
  };

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };



  return (
    <>
      <ScrollToTop />
      <SiteFavicon />
      <div className={`App${isStandalonePage ? ' App--standalone' : ''}${isLogisticsSite ? ' logistics-site' : ''}`}>
        {isLogisticsSite && (
          <LogisticsHeader
            text={text}
            lang={lang}
            isMenuOpen={isMenuOpen}
            onToggleMenu={toggleMenu}
            onCloseMenu={closeMenu}
            onLangChange={handleLangChange}
          />
        )}

        {/* Main Content */}
        <main className={isStandalonePage ? 'main--standalone' : undefined}>
          <Suspense fallback={<div style={{ minHeight: '35vh', background: '#07131f' }} />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/marketing/*" element={<MarketingApp />} />
              <Route path="/logistics" element={<LogisticsHome text={text} lang={lang} />} />
              <Route path="/projects" element={<Projects text={text} lang={lang} />} />
              <Route path="/contact" element={<LogisticsContact text={text} lang={lang} />} />

              <Route path="/about" element={<About lang={lang} />} />
              <Route path="/services" element={<LogisticsServices text={text} lang={lang} />} />
              <Route path="/freight-services" element={<FreightServices />} />
              <Route path="/logistics-solutions" element={<LogisticsSolutions />} />
              <Route path="/comprehensive-services" element={<ComprehensiveServices />} />
              <Route path="/terms" element={<Terms text={text} lang={lang} />} />
              <Route path="/privacy" element={<Privacy text={text} lang={lang} />} />
            </Routes>
          </Suspense>
        </main>

        {isLogisticsSite && <LogisticsFooter text={text} />}
        {!isStandalonePage && !isLogisticsSite && <Footer text={text} lang={lang} />}
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
